import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Clock, CheckCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

var API = "/api";

function formatPrice(price) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function OrderItemRow({ item }) {
  var imgSrc = item.image || null;
  var itemTotal = item.price * item.quantity;
  return (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-[#1c1c1c] rounded-sm overflow-hidden">
        {imgSrc ? <img src={imgSrc} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-[#3f3f46]" /></div>}
      </div>
      <div className="flex-1">
        <p className="text-white text-sm">{item.name}</p>
        <p className="text-[#A1A1AA] text-xs">Qty: {item.quantity}</p>
      </div>
      <p className="text-white font-mono">{formatPrice(itemTotal)}</p>
    </div>
  );
}

function OrderCard({ order }) {
  var items = order.items || [];
  var isConfirmed = order.status === "confirmed";
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-[#121212] border border-white/5 rounded-sm p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <p className="font-mono text-[#C0A062] text-sm">{order.order_id}</p>
          <p className="text-[#A1A1AA] text-sm">{formatDate(order.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          {isConfirmed ? (
            <span className="inline-flex items-center gap-1 text-[#8CB369]"><CheckCircle className="w-4 h-4" />Confirmed</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[#C0A062]"><Clock className="w-4 h-4" />Pending</span>
          )}
        </div>
      </div>
      <div className="space-y-3">
        {items.map(function(item) { return <OrderItemRow key={item.product_id || item.name} item={item} />; })}
      </div>
      <div className="mt-4 pt-4 border-t border-white/10 flex justify-between">
        <span className="text-[#A1A1AA]">Total</span>
        <span className="font-heading font-bold text-[#C0A062] text-xl">{formatPrice(order.total)}</span>
      </div>
    </motion.div>
  );
}

export default function OrdersPage() {
  var auth = useAuth();
  var [orders, setOrders] = useState([]);
  var [isLoading, setIsLoading] = useState(true);
  var token = auth.token;
  var isAuthenticated = auth.isAuthenticated;

  useEffect(function() {
    if (token) {
      axios.get(API + "/orders", { headers: { Authorization: "Bearer " + token } })
        .then(function(res) { setOrders(res.data); })
        .catch(function() { })
        .finally(function() { setIsLoading(false); });
    } else {
      setIsLoading(false);
    }
  }, [token]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="container-tactical text-center py-24">
          <h2 className="font-heading font-bold text-white text-2xl uppercase mb-4">Sign In Required</h2>
          <p className="text-[#A1A1AA] mb-6">Please sign in to view your order history.</p>
        </div>
      </div>
    );
  }

  var orderCount = orders.length;

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="orders-page">
      <div className="container-tactical">
        <h1 className="font-heading font-black text-4xl md:text-5xl text-white uppercase mb-8">Order History</h1>

        {isLoading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" /></div>
        ) : orderCount === 0 ? (
          <div className="text-center py-24 bg-[#121212] border border-white/5 rounded-sm">
            <Package className="w-16 h-16 text-[#3f3f46] mx-auto mb-4" />
            <h3 className="font-heading font-bold text-white text-xl uppercase mb-2">No Orders Yet</h3>
            <p className="text-[#A1A1AA] mb-6">Start shopping to see your orders here.</p>
            <Link to="/shop" className="btn-primary inline-flex items-center px-6 py-3">Shop Now</Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map(function(order) { return <OrderCard key={order.order_id} order={order} />; })}
          </div>
        )}
      </div>
    </div>
  );
}
